import { WorkflowPageComponent } from './workflow-page/workflow-page.component';
import { UntypedFormGroup, UntypedFormControl, Validators } from '@angular/forms';
import { JobTitleWithTargetDifference } from 'src/shared/models/job-title-with-target-difference.model';
import { RecommendationService } from 'src/shared/services/recommendation.service';
import { Router } from '@angular/router';
import { WorkflowUtilityService } from 'src/shared/services/workflow-utility.service';
import { StaffingPlan } from 'src/shared/models/staffing-plan.model';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { WorkflowConstants } from '../workflow-constants.util';
import { WorkflowPageUtil } from './workflow-page/workflow-page.util';
import { MiscUtil } from 'src/shared/utilities/misc.util';
import { Constants } from 'src/shared/utilities/constants.util';

export class AncillaryEnterCurrentBaseComponent extends WorkflowPageComponent {
    
    public staffForm = new UntypedFormGroup({});
    public staffFormValues$;
    public jobTitlesWithVariance: JobTitleWithTargetDifference[] = [];
    public recommendations: StaffingPlan[];
    public loadingRecommendations: boolean;
    public minValue = WorkflowConstants.UosMinValue;
    public maxValue = WorkflowConstants.UosMaxValue;
    public showFixedStaff = false;
    public enableFixedJobEditing = false;

    constructor(
        protected _recommendationService: RecommendationService,
        protected _router: Router,
        protected _workflowUtilityService: WorkflowUtilityService) {
        super(_router);        
    }    

    getVarianceWarnings() {
        return this._workflowUtilityService.getVarianceWarnings();
    }

    getStaffControlName(jobTitleId: number) {
        return this._workflowUtilityService.getStaffControlName(jobTitleId);
    }

    getRecommendedStaffText(jobTitleId: number) {
        const count = this.getRecommendedStaffValue(jobTitleId);
        this.staffForm.updateValueAndValidity({onlySelf: true, emitEvent: true});
        
        return (count !== null && count !== undefined) ? count : '*';
    }

    getRecommendedStaffValue(jobTitleId: number) : number {
        let staffValue = 0;

        if (this.recommendations) {
            let plans = this.recommendations.filter(x => x.jobTitleId == jobTitleId);
            if (plans && plans.length && plans.length > 0) {
                plans.forEach(s => {
                    staffValue += s.recommended;
                });
                let isVariable = this.shiftRecord.costCenter.variableJobTitles.length>0 &&
                                 this.shiftRecord.costCenter.variableJobTitles.findIndex(i=>i.id==jobTitleId) > -1;
                if(isVariable){
                    staffValue = Math.ceil(staffValue); 
                }
                return staffValue;
            }
        }

        return 0;
    }  

    protected initializeStaffForm() { 
        let validators = [Validators.required, Validators.min(this.minValue), Validators.max(this.maxValue)];               
        
        this.shiftRecord.costCenter.variableJobTitles.forEach(jobTitle => {
            this.staffForm.addControl(this.getStaffControlName(jobTitle.id), new UntypedFormControl(null, validators));
        });

        this.shiftRecord.costCenter.fixedJobTitles.forEach(jobTitle => {
            this.staffForm.addControl(this.getStaffControlName(jobTitle.id), new UntypedFormControl({ value: '', disabled: true }, validators));
        });
      
        // if returning from enter reasons, populate fields
        if (this.shiftRecord.staffingPlans && this.shiftRecord.staffingPlans.length){
            this.shiftRecord.staffingPlans.forEach(plan => {
                let control = this.staffForm.controls[this.getStaffControlName(plan.jobTitleId)];

                if (control) {
                    let valueToUse: number;

                    if(WorkflowPageUtil.hasUosConfiguration(this.shiftRecord.costCenter) && MiscUtil.isValidNonNegativeNumber(plan.projected)){
                        valueToUse = plan.projected;
                    }
                    else if (MiscUtil.isValidNonNegativeNumber(plan.amount)) {
                        valueToUse = plan.amount;
                    }   
                    
                    if (MiscUtil.isValidNonNegativeNumber(valueToUse)) {
                        control.setValue(valueToUse);
                        control.markAsDirty();
                    }                    
                }                
            });            
        }

        this.staffFormValues$ = this.staffForm.valueChanges
        .pipe(
            debounceTime(Constants.DebounceTime), 
            // This is a workaround to this angular bug: https://github.com/angular/angular/issues/12540
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        .subscribe(values => {
            this.updateJobTitlesWithVariance(values);
        });

        this.staffForm.updateValueAndValidity();
    }    

    protected isValidStaffControls(): boolean {
        let invalidFound = false;

        this.shiftRecord.costCenter.jobTitles.forEach(jobTitle => {
            let staffControl = this.staffForm.get(this.getStaffControlName(jobTitle.id));

            if (!staffControl || !MiscUtil.isValidNonNegativeNumber(staffControl.value)) {
                invalidFound = true;
            }
        });
        
        return !invalidFound;
    }

    protected setStaffPlans() {
        let existingStaffingPlans = this.shiftRecord.staffingPlans || [];
        this.shiftRecord.staffingPlans = [];

        this.shiftRecord.costCenter.jobTitles.forEach(jobTitle => {
            let plan = existingStaffingPlans.find(s => s.jobTitleId == jobTitle.id) || new StaffingPlan();
            
            plan.workflowTimeFacility = this.shiftRecord.workflowTimeFacility;
            plan.shiftBlockId = this.shiftRecord.shiftBlockId;
            plan.jobTitleId = jobTitle.id;

            let control = this.staffForm.controls[this.getStaffControlName(jobTitle.id)];

            if (control && MiscUtil.isValidNonNegativeNumber(control.value)) {
                if(WorkflowPageUtil.hasUosConfiguration(this.shiftRecord.costCenter)){
                    plan.projected = control.value;
                }
                else{
                    plan.amount = control.value;
                }
            }               

            plan.recommended = +this.getRecommendedStaffValue(jobTitle.id);

            this.shiftRecord.staffingPlans.push(plan);
        });
    }

    private toggleFixedJobEditing(checkFixedStaffVisibility: boolean) {
        this.enableFixedJobEditing = !this.enableFixedJobEditing;

        // If the fixed staff is not visible then show it.
        if (checkFixedStaffVisibility && !this.showFixedStaff) {
            this.showFixedStaff = true;
        }

        this.shiftRecord.costCenter.fixedJobTitles.map(j => j.id).forEach(jobTitleId => {
            let control = this.staffForm.controls[this.getStaffControlName(jobTitleId)];

            if (control) {
                if (this.enableFixedJobEditing) {
                    control.enable();
                }
                else {
                    control.disable();
                }
            }
        });
    }

    private toggleFixedStaff() {
        this.showFixedStaff = !this.showFixedStaff;
    }    

    private updateFixedJobTitleDefaults() {    
        this.shiftRecord.costCenter.fixedJobTitles.map(j => j.id).forEach(jobTitleId => {
            let fixedStaffControl = this.staffForm.controls[this.getStaffControlName(jobTitleId)];

            if (fixedStaffControl && !fixedStaffControl.dirty) {
                let recommended = this.getRecommendedStaffValue(jobTitleId);
                let current = +fixedStaffControl.value;
  
                if (MiscUtil.isValidNonNegativeNumber(recommended) && MiscUtil.isValidNonNegativeNumber(current)) {
                    fixedStaffControl.setValue(recommended);
                    fixedStaffControl.markAsPristine();
                }
            }
        });
    }    

    protected updateStaffingRecommendation(recommendationMethod: Promise<StaffingPlan[]>) {
        this.loadingRecommendations = true;
        this.staffForm.disable({ emitEvent: false });
        recommendationMethod
            .then(recommendations => {
                this.recommendations = recommendations;                
                this.staffForm.enable({ emitEvent: false });
                this.loadingRecommendations = false;
                this.updateJobTitlesWithVariance(this.staffForm.value);
                this.updateFixedJobTitleDefaults();

                // Because we wholesale enable and disable controls for the staffForm (this.staffForm.disable and this.staffForm.enable)
                // It is causing fixed job titles to ignore their editing status.  By flipping the toggle and then flipping it again
                // when we call toggleFixedJobEditing it ensures that the fixed job title controls will keep their corrected enable or 
                // disabled state.
                this.enableFixedJobEditing = !this.enableFixedJobEditing;
                this.toggleFixedJobEditing(false);
            });
    }

    private updateJobTitlesWithVariance(values){
        var titlesWithVariance = [];

        this.shiftRecord.costCenter.jobTitles.forEach(jobTitle => {
            let control = this.staffForm.controls[this.getStaffControlName(jobTitle.id)];
            let amount: number;
            
            if (control && MiscUtil.isValidNumber(control.value)) {
                amount = +control.value;
            }
            
            let recommended = +this.getRecommendedStaffValue(jobTitle.id);

            if (MiscUtil.isValidNumber(recommended) && MiscUtil.isValidNumber(amount) && amount !== recommended){
                titlesWithVariance.push({
                    jobTitle: jobTitle, 
                    targetDifference: amount - recommended
                });
            }
        });

        this.jobTitlesWithVariance = this._workflowUtilityService.sortJobTitlesWithVariance(titlesWithVariance, this.shiftRecord.costCenter.fixedJobTitles, this.shiftRecord.costCenter.variableJobTitles)
        this._workflowUtilityService.setJobTitlesWithVariance(this.jobTitlesWithVariance);
    }
}
